/**
 * Quick Review Interface
 * Displays critical issues for quick validation (3-5 minutes)
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Loader2,
} from 'lucide-react';
import { startReview, completeQuickReview } from '../../lib/api/domainPacksApi';

interface CriticalIssue {
  issue_id: string;
  severity: 'critical';
  title: string;
  subdomain_name: string;
  subdomain_id: string;
  description: string;
  recommendation: string;
}

interface QuickReviewInterfaceProps {
  domainPackId: string;
  domainName: string;
  reviewerName: string;
  reviewerEmail: string;
  onComplete: () => void;
  onCancel: () => void;
}

const QuickReviewInterface: React.FC<QuickReviewInterfaceProps> = ({
  domainPackId,
  domainName,
  reviewerName,
  reviewerEmail,
  onComplete,
  onCancel,
}) => {
  const queryClient = useQueryClient();
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [criticalIssues, setCriticalIssues] = useState<CriticalIssue[]>([]);
  const [acknowledgedIssues, setAcknowledgedIssues] = useState<Set<string>>(new Set());
  const [quickNotes, setQuickNotes] = useState('');
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Start review mutation
  const startReviewMutation = useMutation({
    mutationFn: () => startReview(domainPackId, {
      review_mode: 'quick',
      reviewer_name: reviewerName,
      reviewer_email: reviewerEmail,
    }),
    onSuccess: (data) => {
      setReviewId(data.review_id);
      setCriticalIssues(data.critical_issues || []);
      setStartTime(new Date());
    },
    onError: (error: any) => {
      alert(`Failed to start review: ${error.message || 'Unknown error'}`);
      onCancel();
    },
  });

  // Complete review mutation
  const completeReviewMutation = useMutation({
    mutationFn: (outcome: 'approved' | 'flagged' | 'rejected') =>
      completeQuickReview(reviewId!, {
        review_id: reviewId!,
        outcome,
        critical_issues_acknowledged: Array.from(acknowledgedIssues),
        quick_notes: quickNotes || undefined,
        time_spent_seconds: Math.floor(elapsedTime),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domain-pack-quality', domainPackId] });
      queryClient.invalidateQueries({ queryKey: ['domain-pack-reviews', domainPackId] });
      onComplete();
    },
    onError: (error: any) => {
      alert(`Failed to complete review: ${error.message || 'Unknown error'}`);
    },
  });

  // Start review on mount
  useEffect(() => {
    startReviewMutation.mutate();
  }, []);

  // Timer
  useEffect(() => {
    if (!startTime) return;

    const interval = setInterval(() => {
      const elapsed = (new Date().getTime() - startTime.getTime()) / 1000;
      setElapsedTime(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  const toggleIssue = (issueId: string) => {
    const newAcknowledged = new Set(acknowledgedIssues);
    if (newAcknowledged.has(issueId)) {
      newAcknowledged.delete(issueId);
    } else {
      newAcknowledged.add(issueId);
    }
    setAcknowledgedIssues(newAcknowledged);
  };

  const handleOutcome = (outcome: 'approved' | 'flagged' | 'rejected') => {
    // Validate all critical issues acknowledged
    if (acknowledgedIssues.size < criticalIssues.length) {
      alert(`Please acknowledge all ${criticalIssues.length} critical issues before proceeding.`);
      return;
    }

    if (outcome === 'rejected' || outcome === 'flagged') {
      if (!quickNotes.trim()) {
        if (!confirm('You have not provided any notes. Continue without notes?')) {
          return;
        }
      }
    }

    if (confirm(`${outcome === 'approved' ? 'Approve' : outcome === 'flagged' ? 'Flag' : 'Reject'} this domain pack for publication?`)) {
      completeReviewMutation.mutate(outcome);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const allAcknowledged = acknowledgedIssues.size === criticalIssues.length;
  const isLoading = startReviewMutation.isPending || completeReviewMutation.isPending;

  if (startReviewMutation.isPending) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-neutral-200">
      {/* Header */}
      <div className="border-b border-neutral-200 p-6 bg-gradient-to-r from-primary-50 to-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">Quick Review</h2>
            <p className="text-neutral-600 mt-1">{domainName}</p>
          </div>
          <div className="flex items-center gap-4">
            {startTime && (
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-neutral-200">
                <Clock className="w-5 h-5 text-primary-600" />
                <span className="text-lg font-semibold text-neutral-900">{formatTime(elapsedTime)}</span>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 bg-neutral-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(acknowledgedIssues.size / Math.max(criticalIssues.length, 1)) * 100}%` }}
            />
          </div>
          <span className="text-sm font-medium text-neutral-700">
            {acknowledgedIssues.size}/{criticalIssues.length} Issues Reviewed
          </span>
        </div>
      </div>

      {/* Critical Issues List */}
      <div className="p-6">
        {criticalIssues.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-neutral-900 mb-2">
              No Critical Issues Found
            </h3>
            <p className="text-neutral-600">
              This domain pack has no critical issues requiring attention.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-semibold text-neutral-900">
                Critical Issues ({criticalIssues.length})
              </h3>
            </div>
            <p className="text-sm text-neutral-600 mb-6">
              Review each critical issue and acknowledge before proceeding with your decision.
            </p>

            <div className="space-y-4">
              {criticalIssues.map((issue, index) => (
                <div
                  key={issue.issue_id}
                  className={`border-2 rounded-lg p-4 transition-all ${
                    acknowledgedIssues.has(issue.issue_id)
                      ? 'border-green-300 bg-green-50'
                      : 'border-red-200 bg-white hover:border-red-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleIssue(issue.issue_id)}
                      className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                        acknowledgedIssues.has(issue.issue_id)
                          ? 'border-green-600 bg-green-600'
                          : 'border-neutral-300 hover:border-green-600'
                      }`}
                      disabled={isLoading}
                    >
                      {acknowledgedIssues.has(issue.issue_id) && (
                        <CheckCircle className="w-4 h-4 text-white" />
                      )}
                    </button>

                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="text-base font-semibold text-neutral-900">
                          Issue {index + 1}: {issue.title}
                        </h4>
                        <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded font-medium">
                          CRITICAL
                        </span>
                      </div>

                      <div className="text-sm text-neutral-600 mb-2">
                        <span className="font-medium">Subdomain:</span> {issue.subdomain_name}
                      </div>

                      <p className="text-sm text-neutral-700 mb-3">
                        {issue.description}
                      </p>

                      <div className="bg-blue-50 border border-blue-200 rounded p-3">
                        <p className="text-xs font-medium text-blue-900 mb-1">RECOMMENDATION</p>
                        <p className="text-sm text-blue-800">{issue.recommendation}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Quick Notes */}
      <div className="px-6 pb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Quick Notes (optional)
        </label>
        <textarea
          value={quickNotes}
          onChange={(e) => setQuickNotes(e.target.value)}
          placeholder="Add any additional notes about this review..."
          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          rows={3}
          disabled={isLoading}
        />
        <p className="text-xs text-neutral-500 mt-1">
          {quickNotes.length} characters
        </p>
      </div>

      {/* Action Buttons */}
      <div className="border-t border-neutral-200 p-6 bg-neutral-50">
        {!allAcknowledged && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800">
              Please acknowledge all {criticalIssues.length} critical issues before making a decision.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-neutral-700 hover:bg-neutral-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={() => handleOutcome('rejected')}
              disabled={isLoading || !allAcknowledged}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {completeReviewMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <XCircle className="w-5 h-5" />
              )}
              Reject
            </button>

            <button
              onClick={() => handleOutcome('flagged')}
              disabled={isLoading || !allAcknowledged}
              className="flex items-center gap-2 px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {completeReviewMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <AlertTriangle className="w-5 h-5" />
              )}
              Flag for Review
            </button>

            <button
              onClick={() => handleOutcome('approved')}
              disabled={isLoading || !allAcknowledged}
              className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {completeReviewMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <CheckCircle className="w-5 h-5" />
              )}
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickReviewInterface;
